<template>
  <v-dialog
    content-class="add-node-popup"
    :value="visiblePopup"
    @keydown.esc="onNoClickButton"
    @click:outside='onNoClickButton'
    :max-width="width"
  >
    <v-card class="popup-wrapper" :style='{"max-width": width}'>
      <div class='close-action'>
        <span class='close_btn' @click='onNoClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class="title">{{$t(title)}}</div>
      <div class='content'>
        <v-select
          v-model="selectedNodeType"
          :items="nodeTypeOptions"
          item-text="display"
          item-value="value"
          :label="$t('ctc.add.node.popup.select.node.label')"
          :append-icon="'expand_more'"
          dense
          outlined
          autofocus
          return-object
          @change='init(false)'
        />
        <!-- Domain Autocomplete: visible for domain, subdomain, ip, or email types -->
        <v-autocomplete
          v-if="showDomainAutocomplete"
          class-name="domain-select"
          :label="$t('ctc.add.node.popup.select.domain.label')"
          :items='listOfDomains'
          :append-icon="'expand_more'"
          v-model='selectedTld'
          dense
          outlined
          autofocus
          return-object
          @change='loadData(selectedTld, selectedNodeType.value)'
        />
        <!-- Subdomain Autocomplete: visible for subdomain or ip types -->
        <v-autocomplete
          v-if="showSubdomainAutocomplete"
          class-name="domain-select"
          :placeholder="$t('select')"
          :items='subdomainsOptions !== [] ? listOfSubdomains : []'
          :loading='loadingSubdomains'
          :append-icon="'expand_more'"
          :label="$t('ctc.add.node.popup.select.subdomain.label')"
          v-model='selectedSubdomain'
          dense
          outlined
          autofocus
          return-object
          @change='loadHosts(selectedTld, selectedSubdomain)'
        />
        <!-- IP Autocomplete: visible for ip type only -->
        <v-autocomplete
          v-if="showIpAutocomplete"
          class-name="domain-select"
          :placeholder="$t('select')"
          :items='hostsOptions !== [] ? listOfHosts : []'
          :loading='loadingHosts'
          :append-icon="'expand_more'"
          :label="$t('ctc.add.node.popup.select.ip.label')"
          v-model='selectedHost'
          dense
          outlined
          autofocus
          return-object
        />
        <!-- Email Autocomplete: visible for email type only -->
        <v-autocomplete
          v-if="showEmailAutocomplete"
          class-name="domain-select"
          :placeholder="$t('select')"
          :items='emailOptions !== [] ? listOfEmails : []'
          :loading='loadingEmails'
          v-model='selectedEmail'
          :append-icon="'expand_more'"
          :label="$t('ctc.add.node.popup.select.email.label')"
          dense
          outlined
          autofocus
          return-object
          @change='loadEmails(selectedTld)'
        />
      </div>
      <div class="buttons">
        <v-btn v-if='showNoButton' small depressed class="btn-tertiary no-button" @click="onNoClickButton()">
          {{$t(noCaption)}}
        </v-btn>
        <v-btn v-if='showYesButton' small depressed class="btn-primary yes-button" @click="onYesClickButton()" :loading='internalLoading' :disabled="isOkDisabled || internalLoading">
          {{$t(yesCaption)}}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>
<script>
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import { mapGetters } from 'vuex';
import { nodeTypes } from '@/constants/threatCanvas';
import {
  GET_SUBDOMAINS_FOR_TLD,
  REMOVE_SUBDOMAINS_FOR_TLD,
  GET_HOSTS_FOR_SUBDOMAIN,
  REMOVE_HOSTS_FOR_SUBDOMAIN,
  GET_EMAILS_FOR_TLD,
  REMOVE_EMAILS_FOR_TLD,
  LOAD_DOMAINS_FROM_CONFIGURATION,
  CREATE_CTC_NODE,
} from '@/store/project/actionTypes';
import { LOAD_ALL_ORGANIZATIONS } from '@/store/organization/actionTypes';
import { LOAD_DOMAINS } from '@/store/user/actionTypes';

export default {
  name: 'AddNodePopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    title: String,
    yesCaption: String,
    noCaption: String,
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    showNoButton: {
      type: Boolean,
      default: true,
      required: false,
    },
    showYesButton: {
      type: Boolean,
      default: true,
      required: false,
    },
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    width: {
      type: String,
      default: '460px',
      required: false,
    },
  },
  data() {
    return {
      selectedNodeType: '',
      selectedTld: '',
      selectedSubdomain: '',
      subdomainsOptions: [],
      loadingSubdomains: false,
      selectedHost: '',
      hostsOptions: [],
      loadingHosts: false,
      selectedEmail: '',
      emailOptions: [],
      loadingEmails: false,
      internalLoading: false,
      pendingNodeCreation: false,
      creationBaseNode: null,
    };
  },
  async mounted() {
    await this.loadOrganizations();
    await this.loadDomains();
  },
  computed: {
    ...mapGetters(['configurationDomains', 'organizationFilter', 'configurationSubdomains', 'subdomainsForTld', 'hostsForSubdomain', 'emailsForTld', 'lastCTCNode']),
    listOfDomains() {
      return this.configurationDomains.filter(item => this.organizationFilter.includes(item.organization.name)).map(domain => domain.configData).sort();
    },
    nodeTypeOptions() {
      return Object.keys(nodeTypes)
        .filter(key => !key.endsWith('_wrapper'))
        .map(key => ({
          value: nodeTypes[key],
          display: this.$t(`ctc.properties.type.${key}`),
        }));
    },
    // New computed properties for conditional rendering
    showDomainAutocomplete() {
      return this.selectedNodeType && [nodeTypes.domain, nodeTypes.subdomain, nodeTypes.ip, nodeTypes.email].includes(this.selectedNodeType.value);
    },
    showSubdomainAutocomplete() {
      return this.selectedNodeType && [nodeTypes.subdomain, nodeTypes.ip].includes(this.selectedNodeType.value);
    },
    showIpAutocomplete() {
      return this.selectedNodeType && this.selectedNodeType.value === nodeTypes.ip;
    },
    showEmailAutocomplete() {
      return this.selectedNodeType && this.selectedNodeType.value === nodeTypes.email;
    },
    isOkDisabled() {
      if (!this.selectedNodeType) return true;
      if (this.selectedNodeType.value === nodeTypes.custom) {
        return false;
      }
      if (this.selectedNodeType.value === nodeTypes.domain) {
        return !this.selectedTld;
      }
      if (this.selectedNodeType.value === nodeTypes.subdomain) {
        return !this.selectedTld || !this.selectedSubdomain;
      }
      if (this.selectedNodeType.value === nodeTypes.ip) {
        return !this.selectedTld || !this.selectedSubdomain || !this.selectedHost;
      }
      if (this.selectedNodeType.value === nodeTypes.email) {
        return !this.selectedTld || !this.selectedEmail;
      }
      return true;
    },
    listOfSubdomains() {
      const subdomainSet = new Set(this.subdomainsOptions);
      return Array.from(subdomainSet).sort();
    },
    listOfHosts() {
      const hostsSet = new Set(this.hostsOptions);
      return Array.from(hostsSet).sort();
    },
    listOfEmails() {
      const emailSet = new Set(this.emailOptions);
      return Array.from(emailSet).sort();
    },
  },
  methods: {
    async onYesClickButton() {
      this.internalLoading = true;
      this.pendingNodeCreation = true;
      // Record the current lastCTCNode value to detect changes later.
      this.creationBaseNode = this.lastCTCNode;

      const nodeType = this.selectedNodeType.value.toLowerCase();
      let payload = {};
      if (nodeType === nodeTypes.domain) {
        payload = {
          nodeType,
          configData: { data: this.selectedTld },
          tld: this.selectedTld,
        };
      } else if (nodeType === nodeTypes.subdomain) {
        payload = {
          nodeType,
          configData: { data: this.selectedSubdomain },
          tld: this.selectedTld,
        };
      } else if (nodeType === nodeTypes.ip) {
        payload = {
          nodeType,
          configData: { data: this.selectedHost, subdomain: this.selectedSubdomain },
          tld: this.selectedTld,
        };
      } else if (nodeType === nodeTypes.email) {
        payload = {
          nodeType,
          configData: { data: this.selectedEmail },
          tld: this.selectedTld,
        };
      } else if (nodeType === nodeTypes.custom) {
        payload = {
          nodeType,
          configData: { data: 'custom' },
          tld: this.selectedTld,
        };
      } else {
        console.error('Unsupported node type', nodeType);
        this.internalLoading = false;
        this.pendingNodeCreation = false;
        return;
      }

      try {
        await this.$store.dispatch(CREATE_CTC_NODE, payload);
      } catch (error) {
        console.error('Error creating node:', error);
        this.internalLoading = false;
        this.pendingNodeCreation = false;
        return;
      }

      // Set a timeout of 15 seconds to cancel waiting if no new node arrives.
      setTimeout(() => {
        if (this.pendingNodeCreation) {
          this.pendingNodeCreation = false;
          this.internalLoading = false;
          this.init();
        }
      }, 15000);
    },
    onNoClickButton() {
      this.init();
      this.$emit('cancel-add-node-clicked', 'true');
    },
    init(resetNode = true) {
      if (resetNode) {
        this.selectedNodeType = '';
      }
      this.selectedSubdomain = '';
      this.subdomainsOptions = [];
      this.selectedTld = '';
      this.selectedHost = '';
      this.hostsOptions = [];
      this.selectedEmail = '';
      this.emailOptions = '';
      this.loadingHosts = false;
      this.loadingEmails = false;
      this.loadingSubdomains = false;
      this.clearHosts();
      this.clearSubdomains();
      this.clearEmails();
    },
    loadData(tld, nodeType) {
      if ([nodeTypes.subdomain, nodeTypes.ip].includes(nodeType)) {
        this.loadSubdomains(tld);
      }
      if (nodeType === nodeTypes.email) {
        this.loadEmails(tld);
      }
      return true;
    },
    async loadSubdomains(tld) {
      this.loadingSubdomains = true;
      this.selectedSubdomain = '';
      await this.$store.dispatch(GET_SUBDOMAINS_FOR_TLD, { tld });
    },
    async loadHosts(tld, subdomain) {
      this.loadingHosts = true;
      this.selectedHost = '';
      await this.$store.dispatch(GET_HOSTS_FOR_SUBDOMAIN, { tld, subdomain });
    },
    async loadEmails(tld) {
      this.loadingEmails = true;
      await this.$store.dispatch(GET_EMAILS_FOR_TLD, { tld });
    },
    async clearSubdomains() {
      await this.$store.dispatch(REMOVE_SUBDOMAINS_FOR_TLD);
    },
    async clearHosts() {
      await this.$store.dispatch(REMOVE_HOSTS_FOR_SUBDOMAIN);
    },
    async clearEmails() {
      await this.$store.dispatch(REMOVE_EMAILS_FOR_TLD);
    },
    async loadOrganizations() {
      await this.$store.dispatch(LOAD_ALL_ORGANIZATIONS);
    },
    async loadDomains() {
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
    },
  },
  watch: {
    subdomainsForTld: {
      handler() {
        const relevantManualConfigurationSubdomains =
          this.configurationSubdomains
            .filter(subdomain => subdomain.tld === this.selectedTld)
            .map(subdomainObj => subdomainObj.configData);
        this.loadingSubdomains = false;
        this.subdomainsOptions = [...this.subdomainsForTld, ...relevantManualConfigurationSubdomains];
      },
      immediate: true,
      deep: true,
    },
    hostsForSubdomain: {
      handler() {
        this.loadingHosts = false;
        this.hostsOptions = [...this.hostsForSubdomain];
      },
      immediate: true,
      deep: true,
    },
    emailsForTld: {
      handler() {
        this.loadingEmails = false;
        this.emailOptions = [...this.emailsForTld];
      },
      immediate: true,
      deep: true,
    },
    lastCTCNode: {
      handler(newVal) {
        if (this.pendingNodeCreation && newVal && newVal !== this.creationBaseNode) {
          this.pendingNodeCreation = false;
          this.internalLoading = false;
          this.$emit('add-node-clicked', newVal);
          this.init();
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
<style scoped lang='scss'>
@import '~@/assets/styles/design-system';

.add-node-popup {
  .popup-wrapper {
    max-width: rem(460);
    padding: $space-8 $space-10;
    .close-action {
      float: right;
      .close_btn {
        float: right;
        margin-top: rem(-15);
        margin-right: rem(-15);

        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }
    .title {
      color: var(--text-primary);
      font-size: $font-size-base;
      font-weight: 600;
      letter-spacing: 1.12px;
      line-height: $space-4;
      text-transform: uppercase;
      padding-bottom: rem(23);
    }

    .content{
      .domain-select {
        flex:1;
        max-width: 20%;
      }

      .v-input {
        margin-bottom: $space-4;
      }
    }

    .buttons {
      text-align: right;
      padding-top: rem(30);

      .no-button {
        box-sizing: border-box;
        height: $space-8;
        width: rem(86);
        border: $border-width-thin solid var(--brand-primary);
        color: var(--brand-primary);
        border-radius: $radius-sm;
        background-color: var(--bg-primary);
        box-shadow: $shadow-sm;
      }

      .yes-button {
        height: $space-8;
        width: fit-content;
        border-radius: $radius-sm;
        background-color: var(--brand-primary);
        box-shadow: $shadow-sm;
        color: var(--text-inverse);
        font-size: $font-size-sm;
        font-weight: 500;
        letter-spacing: 1.07px;
        line-height: $space-4;
        text-align: center;
        margin-left: $space-2;
      }
    }
  }
}
</style>
